import { Moment } from 'moment';
import { reactive } from 'vue';
import AuthService from '@/general/services/AuthService';
import pushService from '@/general/services/PushService';
import CommonFilters from '@/shared/models/CommonFilters';
import MobilitySolution from '@/shared/models/MobilitySolution';
import Outlet from '@/shared/models/Outlet';
import { isPresent, present } from '@/shared/utils/typescript';
import BookingClient from '../client/BookingClient';
import BookingAction from '../constants/BookingAction';
import BookingProgressCategory from '../constants/BookingProgressCategory';
import BookingStatus from '../constants/BookingStatus';
import Booking, { BookingId } from '../models/Booking';
import BookingContext from '../models/BookingContext';
import BookingOverviewContext from '../models/BookingOverviewContext';

class BookingService {
  async loadOverview(filters: CommonFilters): Promise<BookingOverviewContext> {
    const bookings = await this.loadAll(filters);
    return new BookingOverviewContext(filters, bookings);
  }

  async bindBookingBoardUpdateHandlers(context: BookingOverviewContext): Promise<void> {
    const dealerGroupId = AuthService.getCurrentUser()?.dealerGroupId;
    context.freeHooks.push(
      pushService.bind(`bookings-${dealerGroupId}/reload-scrumboard`, async () => {
        context.bookings = await this.loadAll(context.filters);
      }),
    );
  }

  private async loadAll(filters: CommonFilters): Promise<Array<Booking>> {
    const user = AuthService.getCurrentUser();
    return BookingClient.search(filters, oid => isPresent(oid) ? user.getOutlet(oid) : null);
  }

  async loadCurrent(id: BookingId): Promise<BookingContext> {
    const booking = await BookingClient.findById(id);
    return reactive(new BookingContext(booking)) as BookingContext;
  }

  accept(booking: Booking): Promise<void> {
    return this.applyAction(booking, BookingAction.ACCEPT);
  }

  reject(booking: Booking): Promise<void> {
    return this.applyAction(booking, BookingAction.REJECT);
  }

  private async applyAction(booking: Booking, action: BookingAction): Promise<void> {
    await BookingClient.handle(present(booking.id), action);
    const status = action === BookingAction.ACCEPT ? BookingStatus.ACCEPTED : BookingStatus.REJECTED;
    booking.updateWith({ status });
  }

  async remove(bookingId: BookingId): Promise<void> {
    await BookingClient.handle(bookingId, BookingAction.REMOVE);
  }

  async updateOutlet(booking: Booking, outlet: Outlet): Promise<void> {
    await BookingClient.updateOutlet(present(booking.id), outlet.id);
    booking.updateOutlet(outlet);
  }

  async updateAppointment(booking: Booking, date: Moment): Promise<void> {
    await BookingClient.updateAppointment(present(booking.id), date);
    booking.updateWith({ appointmentOn: date });
  }

  async updateMobilitySolution(booking: Booking, mobilitySolution: MobilitySolution | null, waitsOnPremise: boolean): Promise<void> {
    await BookingClient.updateMobilitySolution(present(booking.id), mobilitySolution, waitsOnPremise);
    booking.updateWith({ mobilitySolution, waitsOnPremise });
  }

  async updateOwnership(booking: Booking, makeOwner: boolean): Promise<void> {
    const { ownedOn, owner } = await BookingClient.updateOwnership(present(booking.id), makeOwner);
    const progressCategory = owner ? BookingProgressCategory.BEING_HANDLED : BookingProgressCategory.REQUESTED;
    booking.updateWith({ owner, ownedOn, progressCategory });
  }
}

export default new BookingService();